"use client";

import type { ImageContent } from "@homeinstead/schemas";

import Image from "next/image";
import { urlFor } from "~www/lib/sanity/sanity-images";
import { DEFAULT_HERO_IMAGES } from "../../CareSeeker/shared/hero-utils";

const DEFAULT_ALT_TEXT = "A Home Instead Care Pro with an adult.";

type HeroPhoto = {
  alt: string;
  url: string;
};

function photoFromImage(
  image: ImageContent | null | undefined,
): HeroPhoto | undefined {
  const asset = image?.image?.asset;
  if (!asset) {
    return undefined;
  }

  return {
    alt: image?.image?.alt || DEFAULT_ALT_TEXT,
    url: urlFor(asset).url(),
  };
}

function defaultPhoto(image: { readonly _ref: string }): HeroPhoto {
  return {
    alt: DEFAULT_ALT_TEXT,
    url: urlFor(image).url(),
  };
}

function ButtonStackHeroImage({
  heroImageLarge,
  heroImageSmall,
}: {
  heroImageLarge?: ImageContent | null;
  heroImageSmall?: ImageContent | null;
}) {
  // Prefer the viewport's own photo, then the other viewport, then the same
  // default the info-panel hero uses when Sanity has no asset.
  const largePhoto =
    photoFromImage(heroImageLarge) ??
    photoFromImage(heroImageSmall) ??
    defaultPhoto(DEFAULT_HERO_IMAGES.large);
  const smallPhoto =
    photoFromImage(heroImageSmall) ??
    photoFromImage(heroImageLarge) ??
    defaultPhoto(DEFAULT_HERO_IMAGES.small);

  return (
    <>
      {/* She sits on the right of the wide photo. Cover at md crops the
          sides, so the window stays on her until xl, where the frame matches
          the desktop crop and center is the designed framing. */}
      <Image
        alt={largePhoto.alt}
        className="hidden object-cover object-[75%_center] md:block xl:object-center"
        fill
        priority
        sizes="100vw"
        src={largePhoto.url}
      />
      {/* The phone frame matches this photo. A wider stacked frame is short,
          and center cover crops her face, so the window stays on the upper body. */}
      <Image
        alt={smallPhoto.alt}
        className="object-cover object-[center_15%] md:hidden"
        fill
        priority
        sizes="100vw"
        src={smallPhoto.url}
      />
    </>
  );
}

export { ButtonStackHeroImage };
